/**
 * Etat de compatibilite d'un jeu, d'apres le registre officiel de Play!.
 *
 * La copie du registre (compatibilite.json, 2 661 titres) est servie a la
 * racine du site, hors du bundle React : elle est deja la pour la page
 * /compatibilite, on la reutilise ici plutot que d'en embarquer une seconde.
 *
 * Le chargement est paresseux et ne bloque jamais l'affichage : la
 * bibliotheque s'affiche sans la colonne, qui se remplit ensuite. Un echec
 * (fichier absent, hors ligne) laisse simplement la colonne vide — ce n'est
 * pas une condition d'erreur.
 */

/** Cles telles qu'elles figurent dans le JSON genere. */
export type CompatState = 'jouable' | 'en-jeu' | 'charge' | 'intro' | 'rien' | 'inconnu';

export interface CompatEntry {
	code: string;
	nom: string;
	etat: CompatState;
	causes: string[];
}

/**
 * Un code disque se presente sous plusieurs formes selon la source :
 * « SLES-504.80 » lu dans l'ISO, « SLES-50480 » dans le registre. On ramene
 * tout a des majuscules sans separateur pour que la comparaison aboutisse.
 */
export function normalizeCode(code: string): string {
	return code.toUpperCase().replace(/[^A-Z0-9]/g, '');
}

let index: Map<string, CompatEntry> | null = null;
let pending: Promise<Map<string, CompatEntry>> | null = null;

/**
 * Charge et indexe le registre. Les appels concurrents partagent la meme
 * requete : la bibliotheque en declenche un par ligne affichee.
 */
export function loadCompat(): Promise<Map<string, CompatEntry>> {
	if (index) return Promise.resolve(index);
	if (pending) return pending;

	pending = fetch('/compatibilite.json', { cache: 'force-cache' })
		.then((response) => {
			if (!response.ok) throw new Error('registre indisponible');
			return response.json();
		})
		.then((raw: any) => {
			const map = new Map<string, CompatEntry>();
			for (const game of raw?.jeux ?? []) {
				const code = normalizeCode(String(game.code ?? ''));
				if (!code) continue;
				map.set(code, {
					code: String(game.code),
					nom: String(game.nom ?? ''),
					etat: (game.etat ?? 'inconnu') as CompatState,
					causes: Array.isArray(game.causes) ? game.causes.map(String) : [],
				});
			}
			index = map;
			return map;
		})
		.catch(() => {
			// Absence de registre = colonne vide, pas une erreur affichable.
			index = new Map();
			return index;
		});

	return pending;
}

/** Consultation synchrone, une fois le registre charge. */
export function lookupCompat(code: string): CompatEntry | null {
	if (!index || !code) return null;
	return index.get(normalizeCode(code)) ?? null;
}

/**
 * Couleur d'un etat. Elles suivent la page /compatibilite pour qu'un meme
 * etat ait la meme couleur partout sur le site.
 */
export const COMPAT_COLOR: Record<CompatState, string> = {
	jouable: '#4ade80',
	'en-jeu': '#a3e635',
	charge: '#fbbf24',
	intro: '#fb923c',
	rien: '#f87171',
	inconnu: '#6b7280',
};

/** Cle de traduction correspondant a un etat. */
export const COMPAT_KEY: Record<CompatState, string> = {
	jouable: 'compat.playable',
	'en-jeu': 'compat.ingame',
	charge: 'compat.loadable',
	intro: 'compat.intro',
	rien: 'compat.nothing',
	inconnu: 'compat.untested',
};
